const { Core, onPlyrLoad, Utils } = Global;

customElements.define('background-video', class extends Core {

    elements = {
        $: ['template', 'content']
    }

    propTypes = {
        'desktop-only': Boolean
    }

    inited = false;

    render() {
        if (this.prop('desktop-only') && document.documentElement.clientWidth <= 992) {
            this.remove();
            return;
        }

        // Store-hosted videos are driven natively: mounting the <video> right
        // away and playing it muted+playsinline is the only path iOS Safari
        // starts reliably (late-attached sources, players that call play()
        // once, and opacity-0-until-playing elements all get their autoplay
        // permission revoked there). Plyr is only used for youtube/vimeo.
        if (this.$('template').content.querySelector('video')) {
            this._initNativeVideo();
            return;
        }

        this._forwardToPrevTime = this._forwardToPrevTime.bind(this);
        this._handlePlaying = this._handlePlaying.bind(this);
        onPlyrLoad(this._observe.bind(this));
    }

    /* ------------------------- hosted video (native) ------------------------- */

    _initNativeVideo() {
        const mount = this.$('template').content.cloneNode(true).firstElementChild;
        this.$('content').appendChild(mount);
        const video = mount.matches('video') ? mount : mount.querySelector('video');
        this.video = video;

        // The cover image behind this element is the placeholder: with no
        // poster a not-yet-started video paints transparent (iOS also
        // stretches posters to fill the box, ignoring object-fit).
        video.removeAttribute('poster');

        // The muted PROPERTY (not just the attribute) is required for
        // programmatic play on iOS; playsinline is required to autoplay.
        video.defaultMuted = true;
        video.muted = true;
        video.setAttribute('playsinline', '');
        video.loop = true;

        // Reveal immediately: the component stays opacity:0 until the
        // 'inited' state, and iOS refuses to autoplay invisible videos.
        this.inited = true;
        Utils.$state(this, 'inited');

        if (this.prevTime) {
            video.addEventListener('loadedmetadata', () => {
                video.currentTime = this.prevTime;
            }, { once: true });
        }

        this._tryPlay = () => {
            if (!this.video || !this.video.paused || this.pausedByObserver) return;
            const p = this.video.play();
            if (p && p.catch) { p.catch(() => {}); }
        };
        this._onGesture = () => this._tryPlay();
        this._onVisibility = () => { if (!document.hidden) this._tryPlay(); };
        this._gestureEvents = ['touchstart', 'touchend', 'click', 'scroll'];

        // Retry at every readiness milestone, on the first user interaction
        // (Brave and iOS Low Power Mode veto silent autoplay but allow
        // gesture-started playback), and when the tab becomes visible again.
        ['loadedmetadata', 'loadeddata', 'canplay'].forEach((ev) => {
            video.addEventListener(ev, this._tryPlay);
        });
        this._gestureEvents.forEach((ev) => {
            window.addEventListener(ev, this._onGesture, { passive: true });
        });
        video.addEventListener('playing', () => this._removeGestureListeners());
        document.addEventListener('visibilitychange', this._onVisibility);
        window.addEventListener('pageshow', this._tryPlay);

        this._tryPlay();

        // Pause offscreen to save battery, resume when scrolled back.
        this.videoObserver = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                this.pausedByObserver = false;
                this._tryPlay();
            } else {
                this.pausedByObserver = true;
                this.video?.pause();
            }
        }, { rootMargin: '100px' });
        this.videoObserver.observe(this);
    }

    _removeGestureListeners() {
        if (!this._gestureEvents || !this._onGesture) return;
        this._gestureEvents.forEach((ev) => {
            window.removeEventListener(ev, this._onGesture);
        });
    }

    /* --------------------------- embeds (Plyr) --------------------------- */

    _observe() {
        this.intersectionObserver.observe(this);
    }

    _initPlayer([ entry ]) {
        if(!entry.isIntersecting) {
            this.player?.pause();
            return
        }
        if(this.player) {
            this.player.play();
            return
        }
        this._initContainer();
        this.player = new window.Plyr(this.playerContainer, {
            muted: true,
            controls: [],
            clickToPlay: false,
            fullscreen: false,
            settings: []
        });

        this.player.once('ready', this.player.play);
        this.prevTime && this.player.on('play', this._forwardToPrevTime);
        this.player.on('ended', this.player.restart);
        this.player.once('error', this.remove);
        this.player.on('playing', this._handlePlaying);
    }

    _handlePlaying() {
        if(!this.inited) {
            this.inited = true;
            Utils.$state(this, 'inited');
        }
    }

    _initContainer() {
        this.playerContainer = this.$('template').content.cloneNode(true).firstElementChild;
        this.$('content').appendChild(this.playerContainer);
    }

    _forwardToPrevTime() {
        this.player.forward(this.prevTime)
    }

    destroy() {
        if(this.video) {
            this.prevTime = this.video.currentTime;
            this._removeGestureListeners();
            document.removeEventListener('visibilitychange', this._onVisibility);
            window.removeEventListener('pageshow', this._tryPlay);
            this.videoObserver?.disconnect();
            this.video.pause();
            this.video = null;
            this.$('content').innerHTML = '';
        }
        if(this.player) {
            this.prevTime = this.player.currentTime;
            this.player.off('play', this._forwardToPrevTime);
            this.player.off('ended', this.player.restart);
            this.player.off('ready', this.player.play);
            this.player.off('error', this.remove);
            this.player.off('playing', this._handlePlaying);
            this.player.destroy();
            this.playerContainer.remove();
            this.player = null;
        }
        this.inited = false;
        Utils.$state(this, 'inited', false);
        this.intersectionObserver.disconnect();
    }

    get intersectionObserver() {
        return new IntersectionObserver(this._initPlayer.bind(this), {
            rootMargin: '0px -10% 0px 0px'
        });
    }
})
